/**
 * Plan catalogue, exactly as supplied by the product owner.
 *
 * Prices live here and nowhere else. Nothing in this file talks to a payment
 * gateway; selecting a plan writes the simulated state in
 * features/access/mockAccess.js, which is the seam a real checkout replaces.
 */

import { PLAN } from "../access/planModel";

export const INR = (amount) => `₹${amount.toLocaleString("en-IN")}`;

/* --- Individual Tutor: pick your features ------------------------------- */

/**
 * ₹500 per feature, per user, no multi-feature discount.
 *
 * Same model the existing pricing page already runs on
 * (features/pricing/pricing_27_04.js — CUSTOM_TIER), including the rule that
 * Documents is excluded from the billable count.
 */
export const TUTOR_FEATURE_PRICE = 500;
export const TUTOR_VALIDITY = "1 Year";

/** Colours are the School dashboard's own module colours — same site language. */
export const TUTOR_PAID_FEATURES = [
  {
    id: "question_papers",
    name: "Create Question Papers",
    usage: "50 papers",
    icon: "/images/questionpaper.png",
    color: "#da5252",
  },
  {
    id: "timetable",
    name: "Create Timetable",
    usage: "Unlimited",
    icon: "/images/timetable.png",
    color: "#1c498d",
  },
  {
    id: "teacher_planner",
    name: "Create Teachers Planner",
    usage: "Unlimited",
    icon: "/images/teacherplanner.png",
    color: "#d4166b",
  },
  {
    id: "documents",
    name: "Create Documents",
    usage: "Unlimited",
    icon: "/images/documents.png",
    color: "#018bd6",
  },
];

/** Free, and never part of the billable feature count. */
export const TUTOR_FREE_FEATURE = {
  id: "documents",
  name: "Documents & Correspondences",
  icon: "/images/documents.png",
  color: "#018bd6",
};

export const TUTOR_PLAN_META = {
  id: "tutor-custom",
  plan: PLAN.TUTOR,
  name: "Individual Tutor",
};

/**
 * Coupon rules, shared by both plans.
 *
 * `coupon` is `{ code, discount }` where discount is either "10%" or a flat
 * amount — the two shapes the existing pricing page accepts. A coupon that
 * would take the bill to zero leaves ₹1, matching that page's behaviour.
 */
const applyCouponTo = (subtotal, coupon) => {
  if (!coupon || subtotal === 0) {
    return { discount: 0, total: subtotal };
  }

  const raw = coupon.discount;
  let reduced;
  if (typeof raw === "string" && raw.includes("%")) {
    const pct = parseFloat(raw.replace("%", ""));
    reduced = Math.round(subtotal - (subtotal * pct) / 100);
  } else {
    reduced = Math.max(0, subtotal - parseFloat(raw));
  }

  const total = reduced <= 0 ? 1 : reduced;
  return { discount: subtotal - total, total };
};

/**
 * Everything bundled at one price — the counterpart to picking features.
 * Mirrors the "Complete Plan" the existing pricing page already sells.
 */
export const TUTOR_COMPLETE_PLAN = {
  id: "tutor-complete",
  plan: PLAN.TUTOR,
  name: "Complete Plan",
  perUser: 1500,
  storage: "0.5 GB",
  features: ["Question Papers", "Timetable", "Teacher Planner", "Documents"],
};

/** Complete plan: flat per-user price × users. */
export const tutorCompleteQuote = ({ users, coupon }) => {
  const seats = Math.max(1, users);
  const perUser = TUTOR_COMPLETE_PLAN.perUser;
  const subtotal = perUser * seats;
  return { perUser, subtotal, ...applyCouponTo(subtotal, coupon) };
};

/**
 * Build-your-own: billable features × ₹500 × users, in one pure function so it
 * can be tested without rendering anything.
 */
export const tutorQuote = ({ featureCount, users, coupon }) => {
  const count = Math.max(0, featureCount);
  const seats = Math.max(1, users);
  const perUser = count * TUTOR_FEATURE_PRICE;
  const subtotal = perUser * seats;
  return { perUser, subtotal, ...applyCouponTo(subtotal, coupon) };
};